import type { Theme, SxProps } from '@mui/material/styles';

import { m } from 'framer-motion';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { ForbiddenIllustration } from 'src/assets/illustrations';

import { varBounce, MotionContainer } from 'src/components/animate';

import { useUserRole, useUserGroups } from '../hooks';

import type { UserRole } from '../roles';

// ----------------------------------------------------------------------

/**
 * Verknüpfung der Kriterien:
 *  - 'any' (Default): mindestens *eines* der definierten Kriterien
 *    (acceptRoles / acceptGroups) muss erfüllt sein.
 *  - 'all': *alle* definierten Kriterien müssen erfüllt sein.
 *
 * Nicht gesetzte Kriterien (undefined) werden ignoriert.
 * Sind weder Rollen noch Gruppen vorgegeben, ist der Zugriff offen
 * (der Guard hat dann effektiv keine Bedingung).
 */
export type AccessMatch = 'any' | 'all';

export type AccessGuardProps = {
  sx?: SxProps<Theme>;
  /**
   * Optionaler Override für die aktuelle Rolle (z.B. fürs Testing oder
   * Server-Rendering). Sonst wird sie aus `useUserRole()` gelesen.
   */
  currentRole?: UserRole | null;
  /**
   * Rollen, die den Zugriff erlauben. User muss eine davon haben.
   * Undefined = Rolle wird nicht geprüft.
   */
  acceptRoles?: readonly UserRole[];
  /**
   * Group-IDs, die den Zugriff erlauben. User muss in mindestens einer
   * dieser Gruppen sein. Undefined = Gruppen werden nicht geprüft.
   */
  acceptGroups?: readonly string[];
  /**
   * Verknüpfung von acceptRoles und acceptGroups, falls beide gesetzt sind.
   * Default: 'any'.
   */
  match?: AccessMatch;
  /**
   * Rolle, die alle Checks überschreibt (z.B. 'admin' darf immer).
   * Default: 'admin'. Auf `null` setzen, um den Bypass zu deaktivieren.
   */
  bypassRole?: UserRole | null;
  /**
   * Wenn true, wird bei verweigertem Zugriff eine "Permission denied"-Seite
   * gerendert (sinnvoll auf Route-Ebene). Sonst rendert der Guard `null`,
   * um Inhalte einfach zu verstecken.
   */
  hasContent?: boolean;
  children: React.ReactNode;
};

// ----------------------------------------------------------------------

export function AccessGuard({
  sx,
  children,
  hasContent,
  currentRole,
  acceptRoles,
  acceptGroups,
  match = 'any',
  bypassRole = 'admin',
}: AccessGuardProps) {
  const { role: roleFromHook, loading: roleLoading } = useUserRole();
  const { groups, loading: groupsLoading } = useUserGroups();

  const effectiveRole = currentRole !== undefined ? currentRole : roleFromHook;
  const loading = roleLoading || groupsLoading;

  // Während die Auth-Daten laden NICHTS rendern – sonst würde kurzzeitig
  // entweder Inhalt oder die Permission-Denied-Seite aufblinken.
  if (loading) {
    return null;
  }

  if (bypassRole && effectiveRole === bypassRole) {
    return <>{children}</>;
  }

  const checks: boolean[] = [];

  if (acceptRoles) {
    checks.push(!!effectiveRole && acceptRoles.includes(effectiveRole));
  }

  if (acceptGroups) {
    checks.push(acceptGroups.some((g) => groups.includes(g)));
  }

  const allowed =
    checks.length === 0
      ? true
      : match === 'all'
        ? checks.every(Boolean)
        : checks.some(Boolean);

  if (allowed) {
    return <>{children}</>;
  }

  if (!hasContent) {
    return null;
  }

  return (
    <Container
      component={MotionContainer}
      sx={[{ textAlign: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <m.div variants={varBounce('in')}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Permission denied
        </Typography>
      </m.div>

      <m.div variants={varBounce('in')}>
        <Typography sx={{ color: 'text.secondary' }}>
          You do not have permission to access this page.
        </Typography>
      </m.div>

      <m.div variants={varBounce('in')}>
        <ForbiddenIllustration sx={{ my: { xs: 5, sm: 10 } }} />
      </m.div>
    </Container>
  );
}
